import { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DollarSign, Eye, Gamepad2, Image as ImageIcon, Palette, Store } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { PluginBuilderSession } from '../types';
import type { FiasTheme } from '@fias/arche-sdk';
import { getThemeById } from '@fias/arche-sdk';
import { ThemeSelector } from './ThemeSelector';
import { IconPicker } from './IconPicker';
import { AppCategoryFields } from '@/components/AppCategoryFields';
import { GameListingFields, type GameListingValue } from '@/components/GameListingFields';
import { CreativeStudioLink } from '@/components/CreativeStudioLink';

interface PluginSettings {
  name: string;
  pricing: {
    model: 'free' | 'fixed' | 'per_use' | 'no_base_cost' | 'included';
    priceCents?: number;
  };
  visibility: {
    isListed: boolean;
    isPublicAccess: boolean;
    isFullScreen: boolean;
  };
  themeId: string;
  fontPairingId: string;
  customTheme?: FiasTheme;
  /** Listed on the Games page. Absent on sessions from before it existed = not a game. */
  game: GameListingValue;
  /**
   * The Arche Store card: the line under the name, and an app's categories (a
   * game names genres instead). Absent on sessions from before it existed.
   */
  store: { subtitle?: string; categories: string[] };
}

/** The most characters in a store subtitle (STORE_SUBTITLE_MAX on the server). */
const STORE_SUBTITLE_MAX = 40;

interface PluginConfigPanelProps {
  session: PluginBuilderSession | null;
  onSettingsChange: (settings: PluginSettings) => void;
  /** Called when the plugin name changes so the header updates immediately */
  onNameChange?: (name: string) => void;
  /** Called when the short description changes so it persists to currentDescription */
  onDescriptionChange?: (description: string) => void;
  /** Called when the expanded description ("About" text) changes */
  onExpandedDescriptionChange?: (expandedDescription: string) => void;
  /** Whether the arche has been published. Currently informational only —
   *  fields stay editable so contributors can fix typos / refresh metadata
   *  and republish. The PATCH endpoint syncs edits straight to the arche row
   *  (`updateArche`), so the changes go live immediately even before the
   *  next bundle publish.
   */
  isPublished?: boolean;
  /** Current arche icon URL — passed through to the IconPicker. */
  imageRef?: string | null;
  /** Bubbled when the IconPicker successfully uploads or removes an icon. */
  onIconChange?: (next: { iconKey: string; imageRef: string } | null) => void;
  /**
   * Guest-trial account: icon upload is full-account-only (an icon is listing
   * material for publishing, and its bytes live on the public icon CDN), so
   * the picker is replaced by a sign-up hint rather than offering an upload
   * that would be refused.
   */
  isGuest?: boolean;
  className?: string;
}

function ThemeConfigCard({
  themeId,
  fontPairingId,
  onThemeChange,
  onFontPairingChange,
}: {
  themeId: string;
  fontPairingId: string;
  onThemeChange: (themeId: string, customTheme?: FiasTheme) => void;
  onFontPairingChange: (fontPairingId: string) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const entry = getThemeById(themeId);

  let themeName: string;
  if (themeId === 'ai-auto') themeName = "AI's Choice";
  else if (themeId === 'no-theme') themeName = 'Custom from Scratch';
  else if (themeId === 'custom') themeName = 'Custom';
  else themeName = entry?.name ?? themeId;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Palette className="h-4 w-4" />
          Appearance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm">
            Theme: <span className="font-medium">{themeName}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
            Change
          </Button>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl h-[85vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>Choose a Theme</DialogTitle>
            </DialogHeader>
            <ThemeSelector
              selectedThemeId={themeId}
              onThemeChange={(id, ct) => onThemeChange(id, ct)}
              selectedFontPairingId={fontPairingId}
              onFontPairingChange={onFontPairingChange}
              className="flex-1 min-h-0"
            />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export function PluginConfigPanel({
  session,
  onSettingsChange,
  onNameChange,
  onDescriptionChange,
  onExpandedDescriptionChange,
  isPublished = false,
  imageRef = null,
  onIconChange,
  isGuest = false,
  className,
}: PluginConfigPanelProps) {
  const currentSettings = (session?.currentSettings || {}) as Partial<PluginSettings> & {
    aiConfigs?: unknown[];
  };
  const hasAiConfigs = ((currentSettings.aiConfigs as unknown[])?.length ?? 0) > 0;

  const [settings, setSettings] = useState<PluginSettings>({
    name: session?.currentName || currentSettings.name || '',
    pricing: currentSettings.pricing || { model: 'free' },
    visibility: currentSettings.visibility || {
      isListed: true,
      isPublicAccess: false,
      isFullScreen: false,
    },
    themeId: ((currentSettings as Record<string, unknown>).themeId as string) ?? 'fias-dark',
    fontPairingId:
      ((currentSettings as Record<string, unknown>).fontPairingId as string) ?? 'system',
    customTheme: (currentSettings as Record<string, unknown>).customTheme as FiasTheme | undefined,
    game: currentSettings.game ?? { isGame: false, genres: [] },
    store: currentSettings.store ?? { categories: [] },
  });

  // Separate local state for name and descriptions so they only commit on blur/Enter
  const [localName, setLocalName] = useState(settings.name);
  const [localSubtitle, setLocalSubtitle] = useState(settings.store.subtitle ?? '');
  const [localDescription, setLocalDescription] = useState(session?.currentDescription || '');
  const [localExpandedDescription, setLocalExpandedDescription] = useState(
    session?.currentExpandedDescription || '',
  );

  const updateSettings = (updates: Partial<PluginSettings>) => {
    const newSettings = { ...settings, ...updates };

    // Auto-disable public access when switching to paid pricing or when AI entities exist
    const isNonFree =
      newSettings.pricing.model !== 'free' &&
      newSettings.pricing.model !== 'included' &&
      newSettings.pricing.model !== 'no_base_cost';
    if (isNonFree && newSettings.visibility.isPublicAccess) {
      newSettings.visibility = { ...newSettings.visibility, isPublicAccess: false };
    }

    setSettings(newSettings);
    onSettingsChange(newSettings);
  };

  /** Capitalize the first letter of each word */
  const toTitleCase = (value: string) => value.replace(/\b\w/g, (char) => char.toUpperCase());

  const commitName = useCallback(() => {
    const trimmed = localName.trim();
    if (!trimmed) return;
    const titleCased = toTitleCase(trimmed);
    setLocalName(titleCased);
    updateSettings({ name: titleCased });
    onNameChange?.(titleCased);
  }, [localName, onNameChange]);

  const commitDescription = useCallback(() => {
    const trimmed = localDescription.trim();
    onDescriptionChange?.(trimmed);
  }, [localDescription, onDescriptionChange]);

  const commitExpandedDescription = useCallback(() => {
    const trimmed = localExpandedDescription.trim();
    onExpandedDescriptionChange?.(trimmed);
  }, [localExpandedDescription, onExpandedDescriptionChange]);

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-4', className)}>
      {/* Name & Description */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="space-y-2">
            <Label htmlFor="plugin-name-config">Arche Name</Label>
            <Input
              id="plugin-name-config"
              type="text"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  commitName();
                  e.currentTarget.blur();
                }
              }}
              placeholder="My Arche"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-description-config">Short Description</Label>
            <Textarea
              id="plugin-description-config"
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
              onBlur={commitDescription}
              placeholder="One-liner shown on marketplace cards"
              className="resize-none"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-expanded-description-config">About</Label>
            <Textarea
              id="plugin-expanded-description-config"
              value={localExpandedDescription}
              onChange={(e) => setLocalExpandedDescription(e.target.value)}
              onBlur={commitExpandedDescription}
              placeholder="Full description shown on your arche's detail page"
              className="resize-none"
              rows={5}
            />
          </div>
          {isPublished && (
            <p className="text-xs text-muted-foreground">
              Edits to name, descriptions, and icon are saved as a draft and apply only after you
              republish — each change goes through AI security review before it becomes visible on
              your live arche.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Icon */}
      {session?.sessionId && isGuest && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Icon
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              An icon is part of publishing your app.{' '}
              <Link to="/auth/signup?reason=create-account" className="text-primary underline">
                Create your free account
              </Link>{' '}
              to add one.
            </p>
          </CardContent>
        </Card>
      )}
      {session?.sessionId && !isGuest && (
        <IconPicker
          sessionId={session.sessionId}
          currentImageRef={imageRef}
          // Icon edits on a published arche stage on the session and
          // get promoted on republish (after AI security review), same
          // as name/description. The picker stays enabled and shows a
          // "Pending — applies after republish" badge while a staged
          // change is queued. See arche-icon-pipeline.ts
          // `skipArcheRowUpdate` and the publish handler's `pendingIcon`
          // promotion.
          pendingIcon={session?.pendingIcon ?? null}
          onUpdated={onIconChange}
        />
      )}

      {/* Pictures — made in FiasWorks Studio, sent back here (ADR-037 D8) */}
      {session?.sessionId && !isGuest && (
        <Card data-testid="builder-pictures-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Pictures
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {session.archId
                ? 'Make pictures in your own styles, then press “Use in” on one: it goes into this arche’s asset library, where your arche can show it.'
                : 'Publish once, then make pictures in your own styles and press “Use in” on one: it goes into this arche’s asset library.'}
            </p>
            <CreativeStudioLink
              className="text-sm"
              from={{ kind: 'builder', id: session.sessionId }}
            />
          </CardContent>
        </Card>
      )}

      {/* Pricing */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Pricing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={
              settings.pricing.model === 'fixed' || settings.pricing.model === 'per_use'
                ? 'fixed'
                : 'no_base_cost'
            }
            onValueChange={(value: 'no_base_cost' | 'fixed') =>
              updateSettings({
                pricing: { ...settings.pricing, model: value },
              })
            }
          >
            <SelectTrigger id="pricing-model">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="no_base_cost">No Base Cost</SelectItem>
              <SelectItem value="fixed">Paid</SelectItem>
            </SelectContent>
          </Select>

          {settings.pricing.model === 'fixed' && (
            <div className="space-y-2">
              <Label htmlFor="plugin-price">Price (cents)</Label>
              <Input
                id="plugin-price"
                type="number"
                min="0"
                value={settings.pricing.priceCents || ''}
                onChange={(e) =>
                  updateSettings({
                    pricing: {
                      ...settings.pricing,
                      priceCents: parseInt(e.target.value) || 0,
                    },
                  })
                }
                placeholder="0"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visibility */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Visibility
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="plugin-listed"
              checked={settings.visibility.isListed}
              onCheckedChange={(checked) =>
                updateSettings({
                  visibility: { ...settings.visibility, isListed: checked as boolean },
                })
              }
            />
            <Label htmlFor="plugin-listed" className="text-sm font-normal">
              Listed (visible in store)
            </Label>
          </div>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="plugin-public"
                checked={settings.visibility.isPublicAccess}
                disabled={hasAiConfigs || settings.pricing.model !== 'free'}
                onCheckedChange={(checked) =>
                  updateSettings({
                    visibility: { ...settings.visibility, isPublicAccess: checked as boolean },
                  })
                }
              />
              <Label
                htmlFor="plugin-public"
                className={cn(
                  'text-sm font-normal',
                  (hasAiConfigs || settings.pricing.model !== 'free') && 'text-muted-foreground',
                )}
              >
                Public Access (viewable when not logged in)
              </Label>
            </div>
            {(hasAiConfigs || settings.pricing.model !== 'free') && (
              <p className="text-xs text-muted-foreground ml-6">
                Only available for free arches that don't require credits.
              </p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="plugin-fullscreen"
              checked={settings.visibility.isFullScreen}
              onCheckedChange={(checked) =>
                updateSettings({
                  visibility: { ...settings.visibility, isFullScreen: checked as boolean },
                })
              }
            />
            <Label htmlFor="plugin-fullscreen" className="text-sm font-normal">
              Full Screen Mode
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Games */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Gamepad2 className="h-4 w-4" />
            Games
          </CardTitle>
        </CardHeader>
        <CardContent>
          <GameListingFields
            idPrefix="plugin"
            value={settings.game}
            onChange={(game) => updateSettings({ game })}
            maxGenres={session?.archeGameGenreLimit}
          />
          {isPublished && session?.archId && (
            <p className="mt-3 text-xs">
              <Link
                to={`/contributor/arches/${session.archId}#store-listings`}
                className="text-primary underline"
              >
                Store listings
              </Link>{' '}
              <span className="text-muted-foreground">
                — give games inside this arche their own place in the Store.
              </span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Arche Store */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Store className="h-4 w-4" />
            Arche Store
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="plugin-store-subtitle">Subtitle</Label>
            <Input
              id="plugin-store-subtitle"
              value={localSubtitle}
              maxLength={STORE_SUBTITLE_MAX}
              placeholder="The line under the name, e.g. Budgets that balance"
              onChange={(e) => setLocalSubtitle(e.target.value.replace(/[\r\n]/g, ''))}
              onBlur={() => {
                const subtitle = localSubtitle.trim();
                if (subtitle !== (settings.store.subtitle ?? '')) {
                  updateSettings({
                    store: { ...settings.store, subtitle: subtitle || undefined },
                  });
                }
              }}
            />
            <p className="text-right text-xs text-muted-foreground">
              {localSubtitle.length}/{STORE_SUBTITLE_MAX}
            </p>
          </div>
          {settings.game.isGame ? (
            <p className="text-xs text-muted-foreground">
              A game is listed under its genres (the Games card above).
            </p>
          ) : (
            <AppCategoryFields
              idPrefix="plugin"
              value={settings.store.categories}
              onChange={(categories) =>
                updateSettings({ store: { ...settings.store, categories } })
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Appearance / Theme */}
      <ThemeConfigCard
        themeId={settings.themeId}
        fontPairingId={settings.fontPairingId}
        onThemeChange={(themeId, customTheme) => updateSettings({ themeId, customTheme })}
        onFontPairingChange={(fontPairingId) => updateSettings({ fontPairingId })}
      />
    </div>
  );
}
